import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProduct } from '@/hooks/useProduct';
import {
  getActivePrograms,
  getActiveEnrollment,
  getProgramPhases,
  getUserCompletions,
  switchProgram as apiSwitchProgram,
  markActivityComplete as apiMarkActivityComplete,
  markActivityStarted as apiMarkActivityStarted,
  getCurrentUser,
  batchGetContentInfo,
} from '@/lib/api';

interface Activity {
  id: string;
  activity_type: string;
  activity_id: string;
  order_index: number;
  is_required: boolean;
  title_override?: string;
  content_title?: string;
  content_description?: string;
  duration?: number;
}

interface Phase {
  id: string;
  name: string;
  description?: string;
  order_index: number;
  duration_days?: number;
  activities: Activity[];
}

interface Program {
  id: string;
  name: string;
  description?: string | null;
  cover_image?: string | null;
  is_active?: boolean | null;
}

interface Enrollment {
  id: string;
  status: string;
  current_phase_id?: string;
  enrolled_at: string;
  started_at?: string;
  program_id: string;
}

interface ActivityCompletion {
  phase_activity_id: string;
  status: string;
  completed_at?: string;
}

interface UserProgramData {
  program: Program | null;
  enrollment: Enrollment | null;
  phases: Phase[];
  completions: ActivityCompletion[];
  loading: boolean;
  nextActivity: Activity | null;
  currentPhase: Phase | null;
  progressPercent: number;
  totalActivities: number;
  completedActivities: number;
  allPrograms: Program[];
  switchProgram: (programId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useUserProgram(): UserProgramData {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const languageLoading = !i18n.isInitialized;
  const { productId } = useProduct();
  const [program, setProgram] = useState<Program | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [completions, setCompletions] = useState<ActivityCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [allPrograms, setAllPrograms] = useState<Program[]>([]);
  const [lastLanguage, setLastLanguage] = useState<string | null>(null);
  const [lastProductId, setLastProductId] = useState<string | null>(null);

  const fetchUserProgram = useCallback(async () => {
    try {
      setLoading(true);
      
      const userResult = await getCurrentUser();
      if (!userResult.success) {
        setLoading(false);
        return;
      }
      const user = userResult.data;

      // Fetch all available programs filtered by user's language and product
      const programsResult = await getActivePrograms(language, productId);
      const availablePrograms = programsResult.success ? programsResult.data : [];
      setAllPrograms(availablePrograms);

      // If no programs available in this language, clear everything and return
      if (availablePrograms.length === 0) {
        setProgram(null);
        setEnrollment(null);
        setPhases([]);
        setCompletions([]);
        setLoading(false);
        return;
      }

      // Check if user has an enrollment
      const enrollmentResult = await getActiveEnrollment(user.id);
      
      if (!enrollmentResult.success || !enrollmentResult.data) {
        // No active enrollment - user needs to manually select a program
        // Don't auto-enroll - let them choose from the available programs list
        setProgram(null);
        setEnrollment(null);
        setPhases([]);
        setCompletions([]);
        setLoading(false);
        return;
      }

      const enrollmentData = enrollmentResult.data;
      
      // Check if the enrolled program still exists and is active
      const enrolledProgram = enrollmentData.program;
      
      // If the enrolled program was deleted or is null, clear the enrollment
      if (!enrolledProgram) {
        console.log('Enrolled program was deleted or not found, clearing enrollment state');
        setProgram(null);
        setEnrollment(null);
        setPhases([]);
        setCompletions([]);
        setLoading(false);
        return;
      }
      
      // Check if the enrolled program matches the current language and product
      const enrolledProgramLanguage = enrolledProgram.language;
      const enrolledProgramMatchesLanguage = enrolledProgramLanguage === language;
      
      // Check if the enrolled program is in the available programs list (active and correct product)
      const isEnrolledProgramAvailable = availablePrograms.some(p => p.id === enrollmentData.program_id);

      // If the enrolled program doesn't match the current language, clear it
      // But still show it if it matches language (even if it's a different product)
      // This handles the case where user switches product context but has an active program
      if (!enrolledProgramMatchesLanguage) {
        console.log('Enrolled program does not match current language, clearing enrollment state');
        setProgram(null);
        setEnrollment(null);
        setPhases([]);
        setCompletions([]);
        setLoading(false);
        return;
      }
      
      // If program matches language but NOT current product context, clear it
      // User should select a program appropriate for the current product
      if (!isEnrolledProgramAvailable) {
        console.log('Enrolled program does not match current product context, clearing enrollment state');
        setProgram(null);
        setEnrollment(null);
        setPhases([]);
        setCompletions([]);
        setLoading(false);
        return;
      }

      // Enrollment matches current language - show it
      setEnrollment({
        id: enrollmentData.id,
        status: enrollmentData.status,
        current_phase_id: enrollmentData.current_phase_id || undefined,
        enrolled_at: enrollmentData.enrolled_at || '',
        started_at: enrollmentData.started_at || undefined,
        program_id: enrollmentData.program_id,
      });
      
      if (enrollmentData.program) {
        setProgram({
          id: enrollmentData.program.id,
          name: enrollmentData.program.name,
          description: enrollmentData.program.description || undefined,
          cover_image: enrollmentData.program.cover_image || undefined,
          is_active: enrollmentData.program.is_active,
        });
      }
      
      await fetchProgramDetails(enrollmentData.program_id, user.id);
    } catch (error) {
      console.error('Error fetching user program:', error);
    } finally {
      setLoading(false);
    }
  }, [language, productId]);

  useEffect(() => {
    // Wait for language to be loaded before fetching programs
    if (languageLoading) return;
    
    // Re-fetch when language or product changes
    if (language !== lastLanguage || productId !== lastProductId) {
      setLastLanguage(language);
      setLastProductId(productId);
      setAllPrograms([]); // Clear programs to force re-render
      fetchUserProgram();
    }
  }, [fetchUserProgram, language, languageLoading, lastLanguage, productId, lastProductId]);

  const fetchProgramDetails = async (programId: string, userId: string) => {
    // Fetch phases with activities
    const phasesResult = await getProgramPhases(programId);

    if (phasesResult.success && phasesResult.data) {
      const phasesData = phasesResult.data;
      
      // Collect all activity IDs for batch fetch
      const activityIds = phasesData.flatMap(p => 
        (p.phase_activities || []).map((a: any) => ({ type: a.activity_type, id: a.activity_id }))
      );

      // Batch fetch content info
      const contentResult = await batchGetContentInfo(activityIds);
      const contentMap = contentResult.success ? contentResult.data : new Map();

      const mappedPhases: Phase[] = phasesData.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description || undefined,
        order_index: p.order_index,
        duration_days: p.duration_days || undefined,
        activities: (p.phase_activities || [])
          .sort((a: any, b: any) => a.order_index - b.order_index)
          .map((a: any) => {
            const content = contentMap.get(a.activity_id);
            return {
              ...a,
              content_title: a.title_override || content?.title || 'Unknown',
              content_description: content?.description,
              duration: content?.duration,
            };
          }),
      }));

      setPhases(mappedPhases);
    }

    // Fetch user's completions
    const completionsResult = await getUserCompletions(userId);
    if (completionsResult.success) {
      setCompletions(completionsResult.data.map(c => ({
        phase_activity_id: c.phase_activity_id,
        status: c.status,
        completed_at: c.completed_at || undefined,
      })));
    }
  };

  const switchProgram = useCallback(async (programId: string) => {
    try {
      const userResult = await getCurrentUser();
      if (!userResult.success) return;

      const result = await apiSwitchProgram(
        userResult.data.id,
        enrollment?.id || null,
        programId
      );

      if (result.success) {
        await fetchUserProgram();
      }
    } catch (error) {
      console.error('Error switching program:', error);
    }
  }, [enrollment, fetchUserProgram]);

  // Calculate next activity and current phase
  const completedIds = new Set(completions.filter(c => c.status === 'completed').map(c => c.phase_activity_id));
  
  let nextActivity: Activity | null = null;
  let currentPhase: Phase | null = null;
  let totalActivities = 0;
  let completedCount = 0;

  for (const phase of phases) {
    for (const activity of phase.activities) {
      totalActivities++;
      if (completedIds.has(activity.id)) {
        completedCount++;
      } else if (!nextActivity) {
        nextActivity = activity;
        currentPhase = phase;
      }
    }
  }

  const progressPercent = totalActivities > 0 ? Math.round((completedCount / totalActivities) * 100) : 0;

  return {
    program,
    enrollment,
    phases,
    completions,
    loading,
    nextActivity,
    currentPhase,
    progressPercent,
    totalActivities,
    completedActivities: completedCount,
    allPrograms,
    switchProgram,
    refetch: fetchUserProgram,
  };
}

export async function markActivityComplete(phaseActivityId: string): Promise<boolean> {
  const userResult = await getCurrentUser();
  if (!userResult.success) return false;

  const result = await apiMarkActivityComplete(userResult.data.id, phaseActivityId);
  return result.success;
}

export async function markActivityStarted(phaseActivityId: string): Promise<boolean> {
  const userResult = await getCurrentUser();
  if (!userResult.success) return false;

  const result = await apiMarkActivityStarted(userResult.data.id, phaseActivityId);
  return result.success;
}
